import { Button } from '@insightai/ui'
import { useState } from 'react'
import { startPkce } from '../../auth/startPkce'

/**
 * M00-16 — Google and Microsoft sign-in.
 *
 * Brand marks are inline SVG rather than icon-font or remote images: a remote
 * logo request on the auth screen adds a blocking round-trip to first paint, and
 * both providers' brand guidelines require the official mark rather than a
 * generic icon.
 */
export function SocialButtons({ mode }: { mode: 'signin' | 'signup' }) {
  const [pending, setPending] = useState<'google' | 'microsoft' | null>(null)
  const verb = mode === 'signin' ? 'Continue' : 'Sign up'

  const start = async (provider: 'google' | 'microsoft') => {
    setPending(provider)
    await startPkce({ identityProvider: provider, register: mode === 'signup' })
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        variant="secondary"
        loading={pending === 'google'}
        onClick={() => void start('google')}
        iconStart={<GoogleMark />}
      >
        {verb} with Google
      </Button>
      <Button
        variant="secondary"
        loading={pending === 'microsoft'}
        onClick={() => void start('microsoft')}
        iconStart={<MicrosoftMark />}
      >
        {verb} with Microsoft
      </Button>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.9-.1-1.5-.2-2.2H12v4.1h6.6c-.1 1.1-.8 2.7-2.4 3.8l-.02.14 3.5 2.7.24.02c2.2-2 3.5-5 3.5-8.6"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2-6.7-4.9l-.14.01-3.6 2.8-.05.13C3.5 21.3 7.4 24 12 24"
      />
      <path
        fill="#FBBC05"
        d="M5.3 14.5c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V9.8L1.6 7C.9 8.6.5 10.3.5 12.2s.4 3.6 1.1 5.2z"
      />
      <path
        fill="#EA4335"
        d="M12 4.7c2.2 0 3.7.9 4.6 1.8l3.3-3.2C17.9 1.4 15.2.2 12 .2 7.4.2 3.5 2.9 1.6 7l3.7 2.9C6.2 6.9 8.9 4.7 12 4.7"
      />
    </svg>
  )
}

function MicrosoftMark() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4">
      <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
      <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
      <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
      <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
    </svg>
  )
}
